import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import {
  EMPTY_PAGE,
  ENTRY_ID,
  TableActionCell,
  TableActionElement,
  TableDraft,
  TableParamConfig,
} from '@markant-platform/shared/table/model';
import { Page, PageFilterOption } from '@markant-platform/shared/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { BehaviorSubject, catchError, delay, map, Observable, of, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CartridgeOverviewInfo } from '@markant-platform/shared/organization/model';
import { RoleConfig, StaticRole } from '@markant-platform/shared/user/model';
import { BackofficeCartridgeService } from '@markant-platform/backoffice/data-access';
import { BackofficeBookingData } from '@markant-platform/shared/backoffice/model';
import {
  OA_ORGANIZATION_BOOKINGS_TABLE,
  PA_ORGANIZATION_BOOKINGS_TABLE,
  ROLE_CONFIG,
} from './configuration/backoffice-organization-bookings-table.config';
import { buildFilterQueryParameter, Filter } from '@markant-platform/shared/ui-filter';
import { UsageRightService } from '@markant-platform/usage-right/data-access';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { UsageRightActivationState } from '@markant-platform/usage-right/model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkantElementsModule } from '@markant/elements-angular';
import { SharedOrganizationFeatureModule } from '@markant-platform/shared/organization/feature';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { OrganizationDetailData, QualificationLevel } from 'data-model';
import { BookingState } from '@markant-platform/cartridge/model';

@Component({
  selector: 'markant-platform-backoffice-organization-bookings-table',
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoModule,
    SharedOrganizationFeatureModule,
    ModalBridgeComponent,
    RouterLink,
    UiPageHeaderComponent,
  ],
  providers: [DatePipe],
  templateUrl: './backoffice-organization-bookings-table.component.html',
  styleUrls: ['./backoffice-organization-bookings-table.component.scss'],
})
export class BackofficeOrganizationBookingsTableComponent implements OnInit {
  //if used as a demo component this value will be used instead of fetched data
  @Input() demoContent?: [Page<CartridgeOverviewInfo>, Record<string, string[]>];

  protected readonly FILTER_KEYS = ['state', 'user', 'organizationId'];

  // this method gets called from a button inside a table cell
  private readonly goTo = (id: string): void => {
    this.router.navigate(['./management', id], { relativeTo: this.route });
  };

  private readonly disabledRow = (data: CartridgeOverviewInfo): boolean => {
    return data.requestType === 'DEMO_REQUEST';
  };

  protected readonly TABLE_ACTIONS: TableActionElement[] = [
    {
      icon: 'm_arrow-right',
      colorScheme: 'dark',
      size: '11',
      function: this.goTo as (...args: unknown[]) => unknown,
    } as TableActionElement,
  ];

  protected tableDraft: TableDraft = PA_ORGANIZATION_BOOKINGS_TABLE;

  id = '';
  organization?: OrganizationDetailData;
  filter: Filter<MarkantSelectOption> = this.getOrganizationBookingFilter();
  isQualificationLevel3 = false;
  totalCount = 0;
  isPlatformAdmin = false;
  isOrganizationAdmin = false;
  isCartridgesDialogOpen = false;
  headline = '';

  forceUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly organizationService: OrganizationService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly cartridgeService: BackofficeCartridgeService,
    private readonly usageRightService: UsageRightService,
    private readonly translocoService: TranslocoService,
    private readonly snackbarService: SnackbarService,
    private readonly datePipe: DatePipe,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.getUserRoles();
    this.getOrganizationId()
      .pipe(
        tap((id: string) => (this.id = id)),
        switchMap(id => this.organizationService.getOrganizationDetailsById(id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: organizationId => {
          this.organization = organizationId;
          this.isQualificationLevel3 = organizationId.qualificationLevel === QualificationLevel.LEVEL_3;
        },
      });
  }

  getUserRoles(): void {
    this.authenticatedUserService
      .getUserRoles()
      .pipe(
        map(roles => roles.map(({ name }) => name)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(roles => {
        const roleConfig = this.getActiveRoleConfig(this.router.url, roles);

        if (!roleConfig) {
          this.snackbarService.showErrorSnackbarWithTransloco('redirect.not-authorized-to-access-view');
          this.router.navigate(['..']);
          return;
        }

        this.isPlatformAdmin = roles.includes(StaticRole.PLATFORM_ADMIN);
        this.isOrganizationAdmin = roles.includes(StaticRole.ORGANIZATION_ADMIN);
        this.headline = roleConfig.additionalConfig['headline'][0];
        this.tableDraft = this.getTableDraft(roleConfig.role);
      });
  }

  getBookingsForOrganization = (tableParams: TableParamConfig): Observable<Page<CartridgeOverviewInfo>> => {
    if (this.demoContent) {
      tableParams.activeFilter = this.demoContent[1];
      return of(this.demoContent[0]).pipe(delay(0));
    }

    const filterQueryParameter = buildFilterQueryParameter(
      this.FILTER_KEYS,
      this.validateStateFilters({
        ...tableParams.activeFilter,
        ...(this.isPlatformAdmin && { organizationId: [this.id] }),
      })
    );

    const { page, searchQuery, sort } = tableParams;

    return this.cartridgeService
      .getBookingsForOrganization(page.index - 1, page.size, searchQuery, filterQueryParameter, sort)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.snackbarService.showErrorSnackbar(error.message);
          return of(EMPTY_PAGE);
        }),
        tap(({ totalCount, filterValues }) => {
          this.totalCount = totalCount;
          this.updateServiceManagerFilter(filterValues);
        }),
        map(res => this.mapBackofficeBookingData(res)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  updateOrganizationData(updatedOrganization: OrganizationDetailData): void {
    this.organization = updatedOrganization;
  }

  openCartridgesDialog(): void {
    this.isCartridgesDialogOpen = true;
  }

  closeCartridgesDialog(): void {
    this.isCartridgesDialogOpen = false;
    this.forceUpdate.next(true);
  }

  private mapBackofficeBookingData(bookings: Page<BackofficeBookingData>): Page<CartridgeOverviewInfo> {
    const result = bookings.result.map(booking => {
      const {
        id,
        bookingDate,
        workflowFinishedAt,
        bookingStartedAt,
        cartridgeVariantName,
        cartridgeVariantDescription,
        bookingState,
        requestType,
        cancellationDate,
        responsibleUserFirstname,
        responsibleUserLastname,
        serviceUpgradeState,
        usagePeriodEndDate,
      } = booking;

      const isEndedDemoBooking = requestType === 'DEMO_REQUEST' && bookingState === BookingState.ENDED;

      return {
        id,
        bookingDate,
        workflowFinishedAt,
        bookingStartedAt,
        requestType,
        name: cartridgeVariantName,
        description: cartridgeVariantDescription ?? '',
        usageRightActivationState: this.usageRightService.getActivationState(
          serviceUpgradeState && bookingState !== 'UPGRADED' && bookingState !== 'CANCELLED'
            ? serviceUpgradeState
            : bookingState
        ),
        validityPeriodEnd: isEndedDemoBooking ? usagePeriodEndDate : cancellationDate,
        validityEndText: this.getValidityEndText(booking),
        serviceManager: [responsibleUserFirstname, responsibleUserLastname].join(' '),
      } as CartridgeOverviewInfo;
    });

    return { ...bookings, result } as Page<CartridgeOverviewInfo>;
  }

  private updateServiceManagerFilter(filterValues?: Record<string, PageFilterOption[]>): void {
    if (!filterValues) {
      return;
    }

    const mappedFilterOptions: MarkantSelectOption[] = (filterValues['filterOption'] || []).map(
      (filter: PageFilterOption) => ({
        value: filter.key,
        representation: filter.label,
      })
    );

    this.filter = {
      ...this.filter,
      user: {
        groupName: 'user',
        translationKey: 'dashboard.cartridge.overviewTable.col.serviceManager',
        filterOptions: mappedFilterOptions,
      },
    };
  }

  // Validates the active filter for filter-key "state" and fallbacks to all states allowed to be displayed
  private validateStateFilters(filter: Record<string, string[]>): Record<string, string[]> {
    const { state } = filter;

    // remap usage right state to booking state to allow proper filtering
    filter['state'] = (state ?? []).flatMap(item => {
      if (item === 'ACTIVE') {
        return ['BOOKED', 'TO_BE_CANCELLED'];
      }
      return item;
    });

    return filter;
  }

  private getValidityEndText(booking: BackofficeBookingData): string | undefined {
    const { bookingState, cancellationDate } = booking;

    if (
      this.usageRightService.getActivationState(bookingState) === UsageRightActivationState.ACTIVE &&
      cancellationDate
    ) {
      const terminateDate = { terminationDate: this.datePipe.transform(cancellationDate) };

      return this.translocoService.translate('cartridge.terminated', terminateDate);
    }

    return undefined;
  }

  private getOrganizationId(): Observable<string> {
    const organizationId = this.route.snapshot.paramMap.get('organizationId');
    return organizationId ? of(organizationId) : this.authenticatedUserService.getOrganizationId();
  }

  private getActiveRoleConfig(url: string, roles: string[]): RoleConfig | undefined {
    return ROLE_CONFIG.find(config => config.routeIdentifier.test(url) && roles.some(role => role === config.role));
  }

  private getTableDraft(role: string): TableDraft {
    const table: TableDraft =
      role === StaticRole.PLATFORM_ADMIN && this.isPlatformAdmin
        ? PA_ORGANIZATION_BOOKINGS_TABLE
        : OA_ORGANIZATION_BOOKINGS_TABLE;

    return {
      ...table,
      rowLink: ['./', 'management', ENTRY_ID],
      headerAndTableCells: [...table.headerAndTableCells, TableActionCell(this.TABLE_ACTIONS)],
      ...((this.isPlatformAdmin || this.isOrganizationAdmin) && {
        disabledRow: this.disabledRow as (obj: unknown) => boolean,
      }),
    };
  }

  private getOrganizationBookingFilter(): Filter<MarkantSelectOption> {
    return Object.assign(
      {},
      {
        state: {
          groupName: 'stateFilter',
          translationKey: 'usageRight.state',
          filterOptions: Object.keys(UsageRightActivationState).map(res => ({
            value: res,
            representation: this.translocoService.translate(`usageRight.activationState.${res}`),
          })),
        },
      }
    );
  }

  protected onUpdateFilterValues(): void {
    this.filter = this.getOrganizationBookingFilter();
  }
}
